import { List, Map, Set } from "immutable";
import {
    Item,
    Item as ProductDisplayFieldPaths,
    MANUFACTURED_SIZES_DELIMITER,
    ROW_HEIGHT,
} from "constants/product-collection";
import { IAllocationDimension, IAllocationDimensionValue, AllocationBag } from "helpers/allocation-dimension";
import { IDoor } from "helpers/door-collection";
import {
    genericParams,
    getColumnWithCommonAttributes,
    ISchemaKey,
    parseSelectedSizes,
    sizeManufacturedGetter,
} from "helpers/product-collection";
import React from "react";
import {
    detailColumnDef,
    SIZE_IDENTIFIER,
    SIZE_TOTALS_IDENTIFIER,
} from "./product-collection-views/product-collection-grid-view/detail-list/helpers";
import {
    IGridParamsQueryParam,
    IPreference,
} from "./product-collection-views/product-collection-grid-view/tool-panels";
import { AgGridReact } from "ag-grid-react";
import {
    ALLOCATION_GRID_COMPONENTS,
    ALLOCATION_GRID_COMPONENTS_VANILLA,
    useColumnTypes,
    defaultColDef,
    HEADER_HEIGHT,
    readAllocations,
    readBulks,
    styles,
    rowClassRules,
    resetSelectedCellsListener,
} from "./product-collection-item-allocation";
import { DATATEST } from "constants/integration";
import { ImageCellRenderer } from "./product-collection-views/product-collection-grid-view/grid-renderers/image-cell-renderer";
import { isValidNumber } from "lib/number";
import { amphibiousGetIn } from "helpers/objUtils";
import Typography from "@material-ui/core/Typography";
import { formatMessage } from "lib/globalize-util";
import { merge, $ } from "glamor";
import { sizeInfoStyle } from "./product-collection-item-sizing";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { theme } from "styles/theme";
import { gridAggFunctions } from "./product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { ColDef, ColGroupDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { PackTypes } from "reducers/collection";
import { IMutateAllocationBagValue } from "actions/collections";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";

interface IProductCollectionItemAllocationTransposed {
    item?: Map<string, any>;
    items: List<Map<string, any>>;
    onMutateCollectionItem: (item) => void;
    onClearAllocations: (itemId: string) => void;
    readOnly: boolean;
    currentAllocationDimensionView: IPreference[];
    doors?: IDoor[];
    dates?: IAllocationDimensionValue[];
    doorClassificationSchema?: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    gridParams?: IGridParamsQueryParam;
    onColumnGroupOpened: (groupId: string, isExpanded: boolean) => void;
    collectionId?: string;
    rowIndex?: number;
    isRollupMode?: boolean;
    sizingAllocationEnabled: boolean;
    allocationDimensions?: IAllocationDimension[];
    merchantPurchaseOrderDoorGroups?: IPurchaseOrderDoorGroup[];
    itemsRevision: number;
    editable: boolean;
    isMobile: boolean;
    commonFieldPath?: string[];
    variationSchema?: any;
    imageSchema?: any;
    otherSchemas: ISchemaKey[];
    sizePackEnabled?: boolean;
    onMutateBagValues: (value: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    rootGridApi: GridApi;
}

const commonColumnAttributes = { pinned: true, editable: false };
const sizingRowClassRules = {
    ...rowClassRules,
    sizeTotalsRow: ({ node }) => node.data.type === SIZE_TOTALS_IDENTIFIER,
    odd: ({ node }) => !!(node.rowIndex % 2),
};
const heightOverride = { height: "28px !important", minHeight: "28px !important" };
export const ProductCollectionItemsAllocationTransposed: React.FunctionComponent<
    IProductCollectionItemAllocationTransposed
> = ({
    item,
    items,
    onMutateCollectionItem,
    onClearAllocations,
    readOnly,
    doors,
    dates,
    doorClassificationSchema,
    gridParams,
    onColumnGroupOpened,
    itemsRevision,
    currentAllocationDimensionView,
    isMobile,
    commonFieldPath = ProductDisplayFieldPaths.FIELDS.PRODUCT_ID,
    imageSchema,
    variationSchema,
    merchantPurchaseOrderDoorGroups,
    otherSchemas,
    sizePackEnabled,
    onMutateBagValues,
    allocationBagRef,
    dimensionsPanelApplied,
    rootGridApi,
}) => {
    const agGridApiRef = React.useRef<GridApi>(null);
    const isLegacyBag = !allocationBagRef;
    const itemId = item?.getIn(ProductDisplayFieldPaths.FIELDS.ID);

    const { itemSizes, rowData } = React.useMemo(() => {
        const { itemSizesArray, rowDataArray } = items.reduce(
            (acc, item) => {
                const sizes =
                    parseSelectedSizes(item.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "")).allSizes ?? [];
                const row = {
                    type: SIZE_IDENTIFIER,
                    [ProductDisplayFieldPaths.ALLOCATIONS[0]]: { current: readAllocations(item) },
                    [ProductDisplayFieldPaths.BULKS[0]]: { current: readBulks(item) },
                    fields: item.getIn(["fields"])?.toJS(),
                    sizes: Set(sizes),
                    rowId: item.getIn(Item.FIELDS.ID),
                };
                return {
                    itemSizesArray: acc.itemSizesArray.concat(sizes),
                    rowDataArray: [...acc.rowDataArray, row],
                };
            },
            { itemSizesArray: [], rowDataArray: [] }
        );
        let itemSizesSet = Set(itemSizesArray);
        const orderedItemSizes = itemSizesArray.reduce((acc, size) => {
            if (itemSizesSet.has(size)) {
                itemSizesSet = itemSizesSet.remove(size);
                return acc.concat([size]);
            }
            return acc;
        }, []);

        return { itemSizes: orderedItemSizes, rowData: rowDataArray };
    }, [items, itemsRevision]); // eslint-disable-line react-hooks/exhaustive-deps

    const pinnedTopRowData = React.useMemo(() => {
        const fieldName = variationSchema?.field_name;
        const sizeTotalsRow = {
            type: SIZE_TOTALS_IDENTIFIER,
            fields: { [fieldName]: { string_value: formatMessage("Size Totals") } },
            sizes: Set([]),
        };
        return [sizeTotalsRow];
    }, [variationSchema]);

    const gridContext = React.useMemo(
        () => ({
            onMutateBagValues,
            allocationBagRef,
        }),
        [onMutateBagValues, allocationBagRef]
    );

    React.useEffect(() => {
        // Overcome AgGrid bug not recognizing context when it is being changed
        // simultaneously with other props
        agGridApiRef.current?.refreshCells();
    }, [gridContext]);

    const handleOnAgGridReady = React.useCallback((event: GridReadyEvent) => {
        agGridApiRef.current = event.api;
        refreshRowData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        refreshRowData();
    }, [rowData, items]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshRowData = () => {
        if (agGridApiRef.current) {
            // Force refresh pinned row as it's not refreshing on paste and undo/redo
            agGridApiRef.current.setPinnedTopRowData(pinnedTopRowData);
        }
    };

    const itemVendorSizes = React.useMemo(
        () => sizeManufacturedGetter(null, { data: item?.toJS() }).split(MANUFACTURED_SIZES_DELIMITER),
        [item]
    );
    const sizePacksApplicable =
        sizePackEnabled &&
        [PackTypes.CasePack, PackTypes.PrePack].includes(
            amphibiousGetIn(item, ProductDisplayFieldPaths.FIELDS.PACK_TYPE)
        );
    const allocationColumnDef: (ColDef | ColGroupDef)[] = React.useMemo(() => {
        const params = { ...genericParams, gridParams };
        const [totalColumn, ...relevantColumns] = detailColumnDef(
            // We won't need a totalizer given we have "Total Units" from now on
            doors,
            dates,
            doorClassificationSchema,
            dimensionsPanelApplied,
            readOnly,
            gridParams,
            currentAllocationDimensionView,
            isMobile,
            merchantPurchaseOrderDoorGroups,
            allocationBagRef,
            itemSizes,
            undefined,
            rootGridApi
        );
        return [
            getColumnWithCommonAttributes({ schemaKey: imageSchema, params }, {}, commonColumnAttributes),
            getColumnWithCommonAttributes(
                { schemaKey: variationSchema, params },
                { display_name: formatMessage("Variations") },
                commonColumnAttributes
            ),
            ...otherSchemas.map((schemaKey) =>
                getColumnWithCommonAttributes({ schemaKey, params }, {}, commonColumnAttributes)
            ),
        ]
            .filter((col) => !!col)
            .concat(relevantColumns as (ColDef | ColGroupDef)[]);
    }, [isMobile, itemSizes, imageSchema, variationSchema, merchantPurchaseOrderDoorGroups, sizePacksApplicable]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCellChange = (event) => {
        const { oldValue, newValue, data } = event;
        // Clean non-numeric and 0
        const cleanedNewValue = isValidNumber(newValue) ? newValue : undefined;

        if (oldValue === cleanedNewValue) {
            return;
        }
        const item = items.find((item) => item.getIn(Item.FIELDS.ID) === amphibiousGetIn(data, Item.FIELDS.ID));
        if (isLegacyBag) {
            onMutateCollectionItem(
                item
                    .setIn(ProductDisplayFieldPaths.ALLOCATIONS, data.allocations?.current)
                    .setIn(ProductDisplayFieldPaths.BULKS, data.bulks?.current)
            );
        }
    };

    const getRowNodeId = React.useCallback((data) => amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.ID), []);
    const tableStyles = React.useMemo(
        () =>
            merge(
                styles,
                ...sizeInfoStyle,
                $(".sizeContainer .ag-theme-balham .doorsGroupTotal--notEditable", {
                    backgroundColor: theme.grid.notEditableBackgroundColor,
                }),
                $(".sizeContainer .ag-theme-balham .sizeColumn--notEditable", {
                    backgroundColor: theme.grid.notEditableBackgroundColor,
                }),
                $(".sizeContainer .ag-theme-balham .sizeTotalsRow", {
                    color: "black !important",
                    fontWeight: "bold",
                    ...heightOverride,
                }),
                $(".sizeContainer .ag-theme-balham .ag-floating-top", heightOverride),
                $(".sizeContainer .ag-theme-balham .odd", {
                    backgroundColor: theme.palette.lighterBlue,
                })
            ),
        []
    );
    const columnTypes = useColumnTypes();
    const containerHeight = React.useMemo(() => HEADER_HEIGHT + ROW_HEIGHT * rowData.length + 2, [rowData.length]);
    const handleCellKeyDown = React.useCallback(
        ({ api, event }) => resetSelectedCellsListener(api, true, { event }),
        []
    );
    return (
        <div {...tableStyles} className={"sizeContainer"}>
            <div data-test={DATATEST.ASSORTMENTS.CDP.SELECTED_SIZES_HEADER} className={"sizeInfo"}>
                <Typography>{formatMessage("Sizes Manufactured") + ":"}</Typography>
                <div data-test={DATATEST.ASSORTMENTS.CDP.SELECTED_SIZES_VALUE} className="field">
                    <span>{itemVendorSizes.join(",")}</span>
                </div>
            </div>
            <div
                data-test={DATATEST.ASSORTMENTS.ALLOCATION_GRID}
                className={`${theme.agGrid.defaultTheme} sizingGrid`}
                style={{ minHeight: `${containerHeight}px` }}
            >
                <AgGridReact
                    onGridReady={handleOnAgGridReady}
                    aggFuncs={gridAggFunctions}
                    columnDefs={allocationColumnDef}
                    rowData={rowData}
                    frameworkComponents={{ imageCellRenderer: ImageCellRenderer, ...ALLOCATION_GRID_COMPONENTS }}
                    components={ALLOCATION_GRID_COMPONENTS_VANILLA}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={handleCellChange}
                    enableRangeSelection={true}
                    rowHeight={ROW_HEIGHT}
                    immutableData={true}
                    getRowNodeId={getRowNodeId}
                    pinnedTopRowData={pinnedTopRowData}
                    rowClassRules={sizingRowClassRules}
                    onCellKeyDown={handleCellKeyDown}
                    enterMovesDownAfterEdit={true}
                    maintainColumnOrder={true}
                    context={gridContext}
                />
            </div>
        </div>
    );
};
